import { forwardRef, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/cn'
import styles from './Input.module.css'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean
  iconLeft?: ReactNode
  /** Rendered inside the control on the right — a unit, a clear button. */
  addonRight?: ReactNode
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid = false, iconLeft, addonRight, className, ...rest },
  ref,
) {
  return (
    <div className={cn(styles.wrapper, invalid && styles.invalid, rest.disabled && styles.disabled, className)}>
      {iconLeft ? <span className={styles.iconLeft}>{iconLeft}</span> : null}
      <input
        ref={ref}
        className={cn(styles.input, iconLeft && styles.hasIconLeft, addonRight && styles.hasAddonRight)}
        aria-invalid={invalid || undefined}
        {...rest}
      />
      {addonRight ? <span className={styles.addonRight}>{addonRight}</span> : null}
    </div>
  )
})

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { invalid = false, className, children, ...rest },
  ref,
) {
  return (
    <div className={cn(styles.wrapper, invalid && styles.invalid, rest.disabled && styles.disabled, className)}>
      <select ref={ref} className={cn(styles.input, styles.select)} aria-invalid={invalid || undefined} {...rest}>
        {children}
      </select>
      <ChevronDown className={styles.selectChevron} aria-hidden="true" />
    </div>
  )
})

export interface TextareaProps extends InputHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean
  rows?: number
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid = false, className, rows = 3, ...rest },
  ref,
) {
  return (
    <div className={cn(styles.wrapper, styles.textareaWrapper, invalid && styles.invalid, className)}>
      <textarea
        ref={ref}
        rows={rows}
        className={cn(styles.input, styles.textarea)}
        aria-invalid={invalid || undefined}
        {...rest}
      />
    </div>
  )
})
